import React, { useEffect, useMemo, useRef } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import Input from '../Form/Input';
import Select from '../Form/Select';
import TextArea from '../Form/TextArea';
import Button from '../Form/Button';
import { CrewEvaluation, CrewEvaluationCategory } from '../../types';
import { useEmployees } from '../../hooks/useEmployees';
import { useBranches } from '../../hooks/useBranches';
import { useAssessmentPoints } from '../../hooks/useAssessmentPoints';
import { EvaluationCategoriesSection, defaultCategories } from './CrewEvaluationFormSections';

const CATEGORY_LABELS: Record<string, string> = {
  technical: 'Technical Skills',
  communication: 'Communication',
  experience: 'Experience',
  education: 'Education',
  leadership: 'Leadership & Teamwork',
  other: 'Other',
};

interface CrewEvaluationFormProps {
  evaluation?: CrewEvaluation;
  onSave: (data: Partial<CrewEvaluation>) => void;
  onCancel: () => void;
  evaluatorRole: 'vessel_manager' | 'chief_manager' | 'hr';
  evaluatorName: string;
  evaluatorId: string;
}

const CrewEvaluationForm: React.FC<CrewEvaluationFormProps> = ({
  evaluation,
  onSave,
  onCancel,
  evaluatorRole,
  evaluatorName,
  evaluatorId
}) => {
  const { data: apiEmployees = [] } = useEmployees({ populate: '*' });
  const { data: apiBranches = [] } = useBranches({ populate: '*' });
  const { data: assessmentPoints = [] } = useAssessmentPoints();

  // Criteria come from the Assessment Points module (employee-facing points),
  // grouped by point category. The hardcoded list is only a fallback.
  const dynamicCategories = useMemo<CrewEvaluationCategory[]>(() => {
    const employeePoints = assessmentPoints.filter(
      (p) => p.isActive && (p.applicableFor === 'employees' || p.applicableFor === 'hiring' || p.applicableFor === 'both')
    );
    const grouped = new Map<string, typeof employeePoints>();
    employeePoints.forEach((p) => {
      const key = p.category || 'other';
      grouped.set(key, [...(grouped.get(key) || []), p]);
    });
    return Array.from(grouped.entries()).map(([category, points], catIndex) => ({
      id: String(catIndex + 1),
      name: CATEGORY_LABELS[category] || category,
      categoryRating: 0,
      items: points.map((p, itemIndex) => ({
        id: `${catIndex + 1}-${itemIndex + 1}`,
        criteriaName: p.name,
        rating: 0,
        comments: '',
      })),
    }));
  }, [assessmentPoints]);

  const vessels = useMemo(() => {
    return apiBranches.filter((b: any) => b.type === 'vessel');
  }, [apiBranches]);

  const employeeOptions = useMemo(() => {
    if (!apiEmployees || apiEmployees.length === 0) return [];
    return apiEmployees.map((emp: any) => ({
      value: emp.documentId || emp.id?.toString() || '',
      label: `${emp.name || 'Unknown'} - ${emp.rank || emp.position || 'Employee'}`
    })).filter((opt: any) => opt.value);
  }, [apiEmployees]);

  const vesselOptions = useMemo(() => {
    if (!vessels || vessels.length === 0) return [];
    return vessels.map((v: any) => ({
      value: v.documentId || v.id?.toString() || '',
      label: v.name || 'Unknown Vessel'
    })).filter((opt: any) => opt.value);
  }, [vessels]);

  const { register, handleSubmit, control, watch, setValue, formState: { errors } } = useForm<CrewEvaluation>({
    defaultValues: evaluation || {
      id: '',
      employeeId: '',
      employeeName: '',
      evaluatorId,
      evaluatorName,
      evaluatorRole,
      vesselId: '',
      vesselName: '',
      evaluationPeriod: { fromDate: '', toDate: '' },
      categories: defaultCategories,
      overallRating: 0,
      overallComments: '',
      status: 'draft',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
  });

  const { fields: categoryFields, update: updateCategory, replace: replaceCategories } = useFieldArray({
    control,
    name: 'categories'
  });

  // Assessment points load asynchronously, so swap in the dynamic criteria
  // once they arrive (new evaluations only — edits keep their saved ratings).
  const appliedDynamicCategories = useRef(false);
  useEffect(() => {
    if (!evaluation && dynamicCategories.length > 0 && !appliedDynamicCategories.current) {
      appliedDynamicCategories.current = true;
      replaceCategories(dynamicCategories);
    }
  }, [evaluation, dynamicCategories, replaceCategories]);

  const watchedCategories = watch('categories');
  const selectedEmployeeId = watch('employeeId');
  const selectedVesselId = watch('vesselId');

  useEffect(() => {
    const selectedEmployee = apiEmployees.find((emp: any) => 
      emp.documentId === selectedEmployeeId || emp.id?.toString() === selectedEmployeeId
    );
    if (selectedEmployee) {
      setValue('employeeName', selectedEmployee.name || '');
    }
  }, [selectedEmployeeId, setValue, apiEmployees]);

  useEffect(() => {
    const selectedVessel = vessels.find((vessel: any) => 
      vessel.documentId === selectedVesselId || vessel.id?.toString() === selectedVesselId
    );
    if (selectedVessel) {
      setValue('vesselName', selectedVessel.name || '');
    }
  }, [selectedVesselId, setValue, vessels]);

  // Options load async while the selects are uncontrolled — re-apply the
  // saved ids once they exist, otherwise the browser keeps the first option.
  // Apply once (per mount): even when RHF state already holds the right id,
  // the DOM select may have snapped to option 0 before options rendered, so
  // setValue must run unconditionally the first time options exist.
  const syncedSelects = useRef({ employee: false, vessel: false });

  useEffect(() => {
    if (syncedSelects.current.employee || !evaluation || !apiEmployees?.length) return;
    const raw = evaluation.employeeId as any;
    const savedId = raw && typeof raw === 'object' ? (raw.documentId || String(raw.id)) : String(raw || '');
    if (!savedId) return;
    const emp = apiEmployees.find((e: any) => e.documentId === savedId || String(e.id) === savedId);
    if (emp) {
      setValue('employeeId', emp.documentId || String(emp.id));
      syncedSelects.current.employee = true;
    }
  }, [apiEmployees, evaluation, setValue]);

  useEffect(() => {
    if (syncedSelects.current.vessel || !evaluation || !vessels.length) return;
    const raw = evaluation.vesselId as any;
    const savedId = raw && typeof raw === 'object' ? (raw.documentId || String(raw.id)) : String(raw || '');
    if (!savedId) return;
    const vessel = vessels.find((v: any) => v.documentId === savedId || String(v.id) === savedId);
    if (vessel) {
      setValue('vesselId', vessel.documentId || String(vessel.id));
      syncedSelects.current.vessel = true;
    }
  }, [vessels, evaluation, setValue]);

  useEffect(() => {
    watchedCategories?.forEach((category, categoryIndex) => {
      const totalRating = category.items.reduce((sum, item) => sum + item.rating, 0);
      const averageRating = category.items.length > 0 ? totalRating / category.items.length : 0;
      
      if (category.categoryRating !== averageRating) {
        updateCategory(categoryIndex, {
          ...category,
          categoryRating: parseFloat(averageRating.toFixed(1))
        });
      }
    });
  }, [watchedCategories, updateCategory]);

  useEffect(() => {
    if (watchedCategories && watchedCategories.length > 0) {
      const totalCategoryRating = watchedCategories.reduce((sum, category) => sum + category.categoryRating, 0);
      const overallRating = parseFloat((totalCategoryRating / watchedCategories.length).toFixed(1));
      setValue('overallRating', overallRating);
    }
  }, [watchedCategories, setValue]);

  const updateItemRating = (categoryIndex: number, itemIndex: number, rating: number) => {
    setValue(`categories.${categoryIndex}.items.${itemIndex}.rating`, rating);
  };

  const onSubmit = (data: CrewEvaluation) => {
    onSave({ ...data, evaluatorId: String(data.evaluatorId || ''), updatedAt: new Date().toISOString(), status: 'submitted' as const });
  };

  const onSaveDraft = (data: CrewEvaluation) => {
    onSave({ ...data, evaluatorId: String(data.evaluatorId || ''), updatedAt: new Date().toISOString(), status: 'draft' as const });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {evaluation ? 'Edit Crew Evaluation' : 'New Crew Evaluation'}
            </h2>
            {evaluation && (
              <p className="mt-1 text-sm text-gray-500">
                Status: <span className="font-medium capitalize">{evaluation.status || 'draft'}</span>
                {evaluation.approvedBy ? ` — approved by ${evaluation.approvedBy}` : ''}
                {evaluation.status === 'rejected' && evaluation.rejectionReason ? ` — ${evaluation.rejectionReason}` : ''}
              </p>
            )}
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* onSubmit is wired so pressing Enter inside a field submits via
            react-hook-form instead of reloading the page and losing input. */}
        <form className="p-6" onSubmit={handleSubmit(onSubmit)}>
          {/* Basic Information */}
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <Select
                label="Employee"
                {...register('employeeId', { required: 'Employee is required' })}
                options={employeeOptions}
                error={errors.employeeId?.message}
              />
              <Select
                label="Vessel"
                {...register('vesselId', { required: evaluatorRole === 'vessel_manager' ? 'Vessel is required for vessel managers' : false })}
                options={vesselOptions}
                error={errors.vesselId?.message}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                type="date"
                label="Evaluation Period - From"
                {...register('evaluationPeriod.fromDate', { required: 'From date is required' })}
                error={errors.evaluationPeriod?.fromDate?.message}
              />
              <Input
                type="date"
                label="Evaluation Period - To"
                {...register('evaluationPeriod.toDate', { required: 'To date is required' })}
                error={errors.evaluationPeriod?.toDate?.message}
              />
            </div>
          </div>

          <EvaluationCategoriesSection
            categoryFields={categoryFields}
            watchedCategories={watchedCategories}
            updateItemRating={updateItemRating}
            register={register}
          />

          {/* Overall Assessment */}
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Overall Assessment</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Overall Rating (Calculated)
                </label>
                <div className="w-20 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-lg font-bold text-green-800">
                    {watch('overallRating') || 0}
                  </span>
                </div>
              </div>
            </div>
            <TextArea
              label="Overall Comments"
              {...register('overallComments')}
              rows={4}
              placeholder="Provide overall assessment and recommendations..."
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <Button type="button" variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="button" variant="secondary" onClick={handleSubmit(onSaveDraft)}>
              Save as Draft
            </Button>
            <Button type="button" variant="primary" onClick={handleSubmit(onSubmit)}>
              Submit for Approval
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CrewEvaluationForm;
